import { Box, Flex } from 'reflexbox';
import { PropsWithChildren, ReactNode, createContext, useState, useContext } from 'react';
import styled from 'styled-components';

import { Popover } from './Popover';
import { IconColor } from './Color';
import { Spinner } from './Spinner';

const Context = createContext({ dismiss: () => {} });

const ItemContainer = styled(Flex)<{ type?: 'primary' | 'destructive'; hasSubtitle?: boolean }>`
	color: ${(props) =>
		props.type === 'destructive' ? props.theme.colors.red : props.theme.colors.black};
	border-radius: 4px;
	cursor: pointer;

	svg {
		display: block;

		width: ${(props) => (props.hasSubtitle ? '30px' : '24px')};
		height: ${(props) => (props.hasSubtitle ? '30px' : '24px')};
	}

	&:hover {
		background: ${(props) => props.theme.colors.lightBlueLight};

		&:active {
			background: ${(props) => props.theme.colors.lightBlue};
		}
	}
`;

export function ContextMenu(props: PropsWithChildren<{ width?: number }>) {
	return (
		<Box p={1} width={props.width}>
			{props.children}
		</Box>
	);
}

const ContextMenuSeparator = styled(Box)`
	margin: 2px 0px;
	background: ${(props) => props.theme.colors.lightBlue};
	height: 1px;
`;

ContextMenu.Separator = ContextMenuSeparator;

function ContextMenuItem(props: {
	icon?: ReactNode;
	title: string | ReactNode;
	description?: string | ReactNode | undefined;
	type?: 'primary' | 'destructive';
	hasSubtitle?: boolean | undefined;
	content?: ReactNode | undefined;
	onClick?: () => Promise<unknown> | void;
}) {
	const [loading, setLoading] = useState(false);
	const context = useContext(Context);

	const handleOnClick = async () => {
		setLoading(true);
		await props.onClick?.();
		setLoading(false);
		context.dismiss();
	};

	return (
		<ItemContainer
			p={2}
			alignItems="center"
			onClick={() => handleOnClick()}
			type={props.type}
			display="flex"
			hasSubtitle={props.hasSubtitle}
		>
			{(props.icon || loading) && (
				<Box flexGrow={0} flexShrink={0} mr={1}>
					{loading ? (
						<Flex justifyContent="center" p={1}>
							<Spinner color={props.type === 'destructive' ? 'red' : undefined} />
						</Flex>
					) : (
						<IconColor color={props.type === 'destructive' ? 'red' : 'black'}>
							{props.icon}
						</IconColor>
					)}
				</Box>
			)}
			<Flex height="100%" flexDirection="column" textAlign="start" pl={2}>
				<div>{props.title}</div>

				{props.description}
			</Flex>
		</ItemContainer>
	);
}

ContextMenu.Item = ContextMenuItem;

function ContextMenuTarget(props: PropsWithChildren<{ menu: ReactNode }>) {
	const [visible, setVisible] = useState(false);

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<Context.Provider value={{ dismiss: () => setVisible(false) }}>
					{props.menu}
				</Context.Provider>
			}
		>
			<Box
				onClick={(event) => {
					setVisible(true);
					event.stopPropagation();
					event.preventDefault();
				}}
			>
				{props.children}
			</Box>
		</Popover.Target>
	);
}

ContextMenu.Target = ContextMenuTarget;
